<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue"
import api from "@/api/axios.js"

// Muestra el soporte adjunto de una solicitud sin salir de la pantalla.
// El archivo se pide al backend como blob (vive en un Drive no compartido),
// así que PDF e imágenes se ven embebidos; Word solo se puede descargar.
const props = defineProps({
  ausencia: { type: Object, default: null }
})
const emit = defineEmits(["cerrar"])

const cargando = ref(false)
const error = ref("")
const url = ref("")
const mimeType = ref("")

const esPdf = computed(() => mimeType.value === "application/pdf")
const esImagen = computed(() => mimeType.value.startsWith("image/"))
const nombreArchivo = computed(() => props.ausencia?.soporteNombreArchivo || "soporte")

function liberarUrl() {
  if (url.value) URL.revokeObjectURL(url.value)
  url.value = ""
  mimeType.value = ""
}

async function cargar(ausencia) {
  liberarUrl()
  error.value = ""
  if (!ausencia) return

  cargando.value = true
  try {
    const { data, headers } = await api.get(`/rrhh/ausencias/${ausencia.id}/soporte`, { responseType: "blob" })
    mimeType.value = (headers["content-type"] || data.type || "").split(";")[0].trim()
    url.value = URL.createObjectURL(new Blob([data], { type: mimeType.value }))
  } catch (err) {
    let mensaje = "Error de conexión con el servidor."
    // Con responseType blob, el JSON de error también llega como Blob.
    const cuerpo = err.response?.data
    if (cuerpo instanceof Blob) {
      try {
        mensaje = JSON.parse(await cuerpo.text()).error || mensaje
      } catch {
        // respuesta sin JSON: se deja el mensaje genérico
      }
    }
    error.value = mensaje
  } finally {
    cargando.value = false
  }
}

watch(() => props.ausencia, cargar, { immediate: true })
onBeforeUnmount(liberarUrl)
</script>

<template>
  <div v-if="ausencia" class="visor-backdrop" @click.self="emit('cerrar')">
    <div class="visor-dialog card shadow-lg">
      <div class="card-header d-flex justify-content-between align-items-center gap-2">
        <h6 class="mb-0 text-truncate">
          <i class="bi bi-paperclip me-1"></i>{{ nombreArchivo }}
        </h6>
        <div class="d-flex align-items-center gap-2">
          <a v-if="url" :href="url" :download="nombreArchivo" class="btn btn-sm btn-outline-primary mb-0">
            <i class="bi bi-download me-1"></i>Descargar
          </a>
          <button type="button" class="btn-close" @click="emit('cerrar')"></button>
        </div>
      </div>

      <div class="card-body visor-body">
        <div v-if="cargando" class="text-center text-muted py-5">
          <div class="spinner-border spinner-border-sm me-2" role="status"></div>
          Cargando soporte...
        </div>
        <div v-else-if="error" class="alert alert-danger mb-0">{{ error }}</div>
        <template v-else-if="url">
          <iframe v-if="esPdf" :src="url" class="visor-pdf" title="Soporte adjunto"></iframe>
          <div v-else-if="esImagen" class="text-center">
            <img :src="url" :alt="nombreArchivo" class="visor-img">
          </div>
          <div v-else class="text-center text-muted py-5">
            Este tipo de archivo no se puede previsualizar en el navegador.<br>
            Usa el botón <strong>Descargar</strong> para abrirlo.
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.visor-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
  padding: 1rem;
}

.visor-dialog {
  width: 100%;
  max-width: 1000px;
  height: 90vh;
  display: flex;
  flex-direction: column;
}

.visor-body {
  flex: 1;
  overflow: auto;
  min-height: 0;
}

.visor-pdf {
  width: 100%;
  height: 100%;
  border: 0;
}

.visor-img {
  max-width: 100%;
  height: auto;
}
</style>
